import { useState, useEffect } from 'react';
import { reviewsAPI } from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { toast } from 'sonner';
import { formatDateTime } from '../../lib/utils';

const PROGRESS_BADGE = {
  NOT_STARTED: 'bg-slate-100 text-slate-700',
  IN_PROGRESS: 'bg-amber-100 text-amber-800',
  COMPLETED: 'bg-emerald-100 text-emerald-800',
};

export function TrainingPanel({ reviewId }) {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [noteDrafts, setNoteDrafts] = useState({});

  const fetchItems = () => {
    reviewsAPI.getReviewTraining(reviewId).then((r) => setItems(r.data || [])).catch(() => setItems([]));
  };
  useEffect(() => { fetchItems(); /* eslint-disable-next-line */ }, [reviewId]);

  if (items.length === 0) return null;

  const updateProgress = async (id, progress) => {
    try {
      await reviewsAPI.updateTrainingProgress(id, progress);
      fetchItems();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update progress');
    }
  };

  const addNote = async (id) => {
    const text = (noteDrafts[id] || '').trim();
    if (!text) return;
    try {
      await reviewsAPI.addTrainingNote(id, text);
      setNoteDrafts({ ...noteDrafts, [id]: '' });
      fetchItems();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to add note');
    }
  };

  return (
    <div className="border-t pt-4 mt-4" data-testid="training-panel">
      <h4 className="text-sm font-semibold mb-2">Training Progress</h4>
      <div className="space-y-3">
        {items.map((it) => {
          const isMine = it.trainer_user_id === user?.id;
          return (
            <div key={it.id} className="border rounded-md p-3" data-testid={`training-item-${it.id}`}>
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium">{it.item_description}</span>
                {isMine ? (
                  <Select value={it.progress} onValueChange={(v) => updateProgress(it.id, v)}>
                    <SelectTrigger className="w-36" data-testid={`training-item-progress-select-${it.id}`}><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="NOT_STARTED">Not Started</SelectItem>
                      <SelectItem value="IN_PROGRESS">In Progress</SelectItem>
                      <SelectItem value="COMPLETED">Completed</SelectItem>
                    </SelectContent>
                  </Select>
                ) : (
                  <Badge className={PROGRESS_BADGE[it.progress]}>{it.progress.replace('_', ' ')}</Badge>
                )}
              </div>
              {(it.notes || []).map((n, idx) => (
                <p key={idx} className="text-xs text-muted-foreground mt-1">{n.by_name}: {n.text} ({formatDateTime(n.at)})</p>
              ))}
              {isMine && (
                <div className="flex gap-2 mt-2">
                  <Textarea value={noteDrafts[it.id] || ''} onChange={(e) => setNoteDrafts({ ...noteDrafts, [it.id]: e.target.value })}
                    placeholder="Add a training note…" maxLength={2000} className="text-sm" data-testid={`training-note-input-${it.id}`} />
                  <Button size="sm" onClick={() => addNote(it.id)} data-testid={`training-note-add-btn-${it.id}`}>Add</Button>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
